import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  CalendarMonth as CalendarIcon,
  CreditCard as PaymentIcon,
  Link as LinkIcon,
  SupportAgent as SupportIcon,
  VideoCall as VideoIcon,
} from '@mui/icons-material';

const cardSx = {
  p: { xs: 2.5, md: 3 },
  background: 'linear-gradient(135deg, rgba(255,255,255,0.88), rgba(255,245,235,0.78))',
  backdropFilter: 'blur(18px)',
  border: '1px solid rgba(200,146,42,0.18)',
  boxShadow: '0 12px 32px rgba(123,45,38,0.08)',
};

const Consultation: React.FC = () => {
  const navigate = useNavigate();
  const [roomCode, setRoomCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [joinedRoomUrl, setJoinedRoomUrl] = useState<string | null>(null);

  const sanitizedRoomCode = useMemo(
    () => roomCode.trim().replace(/[^a-zA-Z0-9-_]/g, ''),
    [roomCode]
  );

  const handleJoinRoom = () => {
    if (!sanitizedRoomCode) {
      setError('Please enter the consultation room code shared with your booking confirmation.');
      return;
    }

    const roomUrl = `https://meet.jit.si/AmruthaKalasam-${sanitizedRoomCode}`;
    setError(null);
    setJoinedRoomUrl(roomUrl);
    window.open(roomUrl, '_blank', 'width=1200,height=800');
  };

  const steps = [
    {
      icon: <CalendarIcon sx={{ color: 'secondary.main', fontSize: 30 }} />,
      title: 'Book a consultation slot',
      description: 'Choose a suitable service and timing that matches your availability.',
    },
    {
      icon: <PaymentIcon sx={{ color: 'secondary.main', fontSize: 30 }} />,
      title: 'Make payment',
      description: 'Secure your appointment with our guided online booking and payment flow.',
    },
    {
      icon: <LinkIcon sx={{ color: 'secondary.main', fontSize: 30 }} />,
      title: 'Receive your consultation link',
      description: 'A meeting room code and joining instructions are shared over email or SMS.',
    },
    {
      icon: <VideoIcon sx={{ color: 'secondary.main', fontSize: 30 }} />,
      title: 'Join at the scheduled time',
      description: 'Open the room from any modern browser and allow camera and microphone access.',
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 }, pt: { xs: 2, md: 3 } }}>
        <Typography variant="h1" sx={{ mb: 1 }}>
          Online Video Consultation
        </Typography>
        <Typography variant="h5" sx={{ color: 'text.secondary', maxWidth: 760, mx: 'auto' }}>
          Meet your astrologer online for a guided, private consultation from the comfort of your home.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gap: 3.5 }}>
        <Card sx={cardSx}>
          <CardContent>
            <Typography variant="h4" sx={{ mb: 2.5 }}>
              How It Works
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2.5 }}>
              {steps.map((step, index) => (
                <Box
                  key={step.title}
                  sx={{
                    p: 2.25,
                    borderRadius: 2.5,
                    backgroundColor: 'rgba(255,255,255,0.72)',
                    border: '1px solid rgba(123,45,38,0.1)',
                    display: 'flex',
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      minWidth: 42,
                      height: 42,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(200,146,42,0.18)',
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                      {step.icon}
                      <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                        {step.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2">{step.description}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        <Card sx={cardSx}>
          <CardContent>
            <Typography variant="h4" sx={{ mb: 1.25 }}>
              Join Consultation
            </Typography>
            <Typography variant="body1" sx={{ mb: 2.5 }}>
              Enter the consultation room code from your booking confirmation to launch your Jitsi Meet session.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr auto' }, gap: 1.5, alignItems: 'start', mb: 1.5 }}>
              <TextField
                label="Consultation Room Code"
                placeholder="Enter your unique room code"
                value={roomCode}
                onChange={(event) => setRoomCode(event.target.value)}
                fullWidth
                helperText="Example: 3f5b2f8e-xxxx or the code shared in your confirmation."
              />
              <Button variant="contained" size="large" startIcon={<VideoIcon />} onClick={handleJoinRoom} sx={{ minHeight: 56 }}>
                Join Room
              </Button>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              Use Chrome, Edge, Safari, or Firefox for best performance. Please allow camera and microphone permissions when prompted.
            </Alert>

            {joinedRoomUrl && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 1.5, color: 'primary.main' }}>
                  Embedded Preview
                </Typography>
                <Box
                  component="iframe"
                  src={joinedRoomUrl}
                  title="Consultation Room"
                  sx={{
                    width: '100%',
                    minHeight: { xs: 360, md: 480 },
                    border: '1px solid rgba(123,45,38,0.16)',
                    borderRadius: 2.5,
                    backgroundColor: '#fff',
                  }}
                  allow="camera; microphone; fullscreen; display-capture"
                />
              </Box>
            )}
          </CardContent>
        </Card>

        <Card sx={cardSx}>
          <CardContent>
            <Typography variant="h4" sx={{ mb: 2 }}>
              Need a Booking First?
            </Typography>
            <Typography variant="body1" sx={{ mb: 2.5 }}>
              Explore available services, choose a slot, and contact us if you need help with payments or joining instructions.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <Button variant="contained" onClick={() => navigate('/services')}>
                Don't have a booking yet?
              </Button>
              <Button variant="outlined" startIcon={<SupportIcon />} onClick={() => navigate('/contact')}>
                Need help?
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Consultation;
